import Role, { RoleAPIResponse, RoleEnum } from "@/classes/role";
import User, { UserAPIResponse } from "@/classes/user";
import { DATA_API_SERVICE } from "@/appConfig";
import APIOrganization from "@/api/auth/organization";
import orderBy from "lodash/orderBy";
import sortBy from "lodash/sortBy";
import UserRrfid, { UserRfidData } from "@/classes/userRrfid";
import API from "@/api/api";

async function findById(id: string) {
  const [userResponse, organizations] = await Promise.all([
    API.get<UserAPIResponse>(`${DATA_API_SERVICE}/v2/user/${id}`),
    APIOrganization.findAllWithFullTree()
  ]);
  const properties = organizations.map(org => org.properties).flat();
  return new User({
    ...userResponse.data,
    role: userResponse.data.role
      ? new Role({
          ...userResponse.data.role,
          rName: userResponse.data.role.rName as RoleEnum
        })
      : null,
    organization: organizations.find(organization => organization.id === userResponse.data.organization) || null,
    property: properties.find(property => property.id === userResponse.data.property) || null
  });
}

async function create(user: User) {
  return await API.post(`${DATA_API_SERVICE}/v2/user/operator`, {
    ...user,
    organization: undefined, // Removing unnecessary data
    property: undefined, // Removing unnecessary data
    role: user.role?.id || null,
    orgUUID: user.organization?.id || null,
    propUUID: user.property?.id || null
  });
}

async function deleteAccount(userId: string) {
  return await API.delete(`${DATA_API_SERVICE}/v2/user/${userId}`);
}

async function clearCache(userId: string) {
  return await API.delete(`${DATA_API_SERVICE}/redis/key/user:${userId}:station`);
}

async function getCache(userId: string) {
  return await API.get(`${DATA_API_SERVICE}/redis/key/user:${userId}:station`);
}

async function updateTracking(userId: string, tracking: boolean) {
  return await API.put(`${DATA_API_SERVICE}/user/tracking/${userId}`, {
    tracking
  });
}

async function listRoles() {
  const response = await API.get<RoleAPIResponse[]>(`${DATA_API_SERVICE}/role`);
  return response.data.map(role => Role.fromAPI(role));
}

async function findAll(role: RoleEnum[] = []) {
  const [usersResponse, organizations] = await Promise.all([
    API.post<UserAPIResponse[]>(
      `${DATA_API_SERVICE}/v2/user/all`,
      {
        role
      },
      { ignoreClearAllCheck: true }
    ),
    APIOrganization.findAllWithFullTree()
  ]);
  const properties = organizations.map(org => org.properties).flat();
  const users = usersResponse.data.map(data =>
    User.fromAPI(
      data,
      organizations.find(organization => organization.id === data.organization),
      properties.find(property => property.id === data.property)
    )
  );
  return sortBy(users, user => user.name);
}

async function update(user: User) {
  return await API.put(`${DATA_API_SERVICE}/v2/user/${user.id}`, {
    ...user,
    organization: undefined, // Removing unnecessary data
    property: undefined, // Removing unnecessary data
    role: user.role?.id || null,
    orgUUID: user.organization?.id || null,
    propUUID: user.property?.id || null
  });
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function removeById(id: string) {
  throw new Error("Not implemented yet");
}

async function getRFIDsForUserId(userId: string) {
  const response = await API.get<UserRfidData[]>(`${DATA_API_SERVICE}/v2/user/${userId}/rfid`);
  return orderBy(
    response.data.map(userRFID => {
      return new UserRrfid(userRFID);
    }),
    [rfid => !!rfid.inactivatedAt, rfid => rfid.createdAt],
    ["asc", "desc"]
  );
}

async function createRFID(userRfid: UserRrfid) {
  return await API.post(`${DATA_API_SERVICE}/v2/user/${userRfid.ukey}/rfid/${userRfid.rawrfid}`);
}

async function removeRFID(userRfid: UserRrfid) {
  return API.delete(`${DATA_API_SERVICE}/v2/user/rfid/${userRfid.id}`);
}

async function reactiveRFID(userRfid: UserRrfid) {
  return API.post(`${DATA_API_SERVICE}/v2/user/rfid/${userRfid.id}/activate`);
}

async function resendForgotPassword(userId: string) {
  return API.post(`${DATA_API_SERVICE}/v2/user/${userId}/resend/password`);
}

async function resendConfirmation(userId: string) {
  return API.post(`${DATA_API_SERVICE}/v2/user/${userId}/resend/confirmation`);
}

const APIUsers = {
  create,
  createRFID,
  findAll,
  findById,
  getRFIDsForUserId,
  listRoles,
  reactiveRFID,
  deleteAccount,
  removeById,
  removeRFID,
  update,
  resendConfirmation,
  resendForgotPassword,
  clearCache,
  getCache,
  updateTracking
};

export default APIUsers;
